import classnames from "classnames";
import { useRef, useState } from "react";
import { Marker } from "react-map-gl/mapbox";

import { useTranslation } from "@/lib/i18n";

import { riskPictureCellLabel } from "./cell-label";
import { riskPictureBadgeUrl } from "./risk-picture-badge";
import { riskPictureItemPosition, riskPictureMarkerZIndex } from "./risk-picture-layers";
import { pointerHasNoHover } from "./marker-tap";
import type { RiskPictureItem } from "./risk-picture-model";
import {
  riskPicturePhenomenonIconUrl,
  riskPicturePhenomenonName,
} from "./risk-picture-phenomena";
import styles from "./risk-picture.module.css";

interface RiskPictureMapMarkerProps {
  item: RiskPictureItem;
  onSelect: () => void;
  previewed?: boolean;
  dimmed?: boolean;
  onHoverItem?: (itemId: string | undefined) => void;
}

export const RiskPictureMapMarkerIcon = ({ item }: { item: RiskPictureItem }) => (
  <span
    className={styles.mapMarkerIcon}
    data-kind={item.kind}
    data-level={item.level}
    style={{
      backgroundImage: `url("${riskPictureBadgeUrl(item.kind, item.level)}")`,
    }}
  >
    <img src={riskPicturePhenomenonIconUrl(item.phenomenon)} alt="" />
  </span>
);

/** Shared closed-stack display for the big map and non-interactive thumbnails. */
export const RiskPictureMapMarkerStackIcon = ({
  item,
  count,
}: {
  item: RiskPictureItem;
  count: number;
}) => (
  <>
    {count > 1 && <span className={styles.markerClusterPlate} aria-hidden />}
    <RiskPictureMapMarkerIcon item={item} />
    {count > 1 && <span className={styles.markerClusterCount}>{count}</span>}
  </>
);

interface RiskPictureMapMarkerCardProps {
  item: RiskPictureItem;
  onSelect: () => void;
  onHoverChange?: (hovered: boolean) => void;
  previewed?: boolean;
  dimmed?: boolean;
  /** The map marker's own click listener. It runs before React's and must share this. */
  activateRef?: { current: () => void };
}

/**
 * Colored phenomenon icon. Hover expands it into the card with the code and
 * phenomenon name. Used on its own marker and inside an overlap fan.
 * Without hover, a tap opens the warning. The click after that tap is ignored.
 */
export const RiskPictureMapMarkerCard = ({
  item,
  onSelect,
  onHoverChange,
  previewed = false,
  dimmed = false,
  activateRef,
}: RiskPictureMapMarkerCardProps) => {
  const { t } = useTranslation();
  const name = riskPicturePhenomenonName(item.phenomenon, t);
  const skipClick = useRef(false);
  const handling = useRef(false);

  const select = () => {
    if (handling.current) return;
    handling.current = true;
    queueMicrotask(() => {
      handling.current = false;
    });
    onSelect();
  };

  const activate = () => {
    if (skipClick.current) return;
    select();
  };

  if (activateRef) activateRef.current = activate;

  return (
    // Fixed to the icon, so the card can grow to the right without moving it.
    <div
      className={styles.mapMarkerAnchor}
      data-marker-card=""
      data-dimmed={dimmed || undefined}
    >
      <div
        className={classnames(styles.mapMarker, previewed && styles.mapMarkerPreviewed)}
        onMouseEnter={() => {
          if (pointerHasNoHover()) return;
          onHoverChange?.(true);
        }}
        onMouseLeave={() => {
          if (pointerHasNoHover()) return;
          onHoverChange?.(false);
        }}
        onPointerUp={(event) => {
          if (!pointerHasNoHover() || event.pointerType === "mouse") return;
          event.stopPropagation();
          // Some phones never fire the click, and the ones that do must not open it twice.
          skipClick.current = true;
          window.setTimeout(() => {
            skipClick.current = false;
          }, 500);
          select();
        }}
        onClick={(event) => {
          event.stopPropagation();
          activate();
        }}
      >
        <RiskPictureMapMarkerIcon item={item} />
        <span className={styles.mapMarkerText}>
          <span className={styles.mapMarkerTextInner}>
            <span className={styles.mapMarkerTitle}>{riskPictureCellLabel(item)}</span>
            <span>{name}</span>
          </span>
        </span>
      </div>
    </div>
  );
};

/**
 * Colored phenomenon icon on the big map. A click, or a tap, opens the warning.
 */
export const RiskPictureMapMarker = ({
  item,
  onSelect,
  previewed = false,
  dimmed = false,
  onHoverItem,
}: RiskPictureMapMarkerProps) => {
  const [hovered, setHovered] = useState(false);
  const [longitude, latitude] = riskPictureItemPosition(item);
  const activateRef = useRef(() => {});

  return (
    <Marker
      longitude={longitude}
      latitude={latitude}
      anchor="center"
      style={{ zIndex: riskPictureMarkerZIndex(item, !dimmed && (hovered || previewed)) }}
      onClick={(event) => {
        event.originalEvent.stopPropagation();
        activateRef.current();
      }}
    >
      <RiskPictureMapMarkerCard
        item={item}
        onSelect={onSelect}
        onHoverChange={(hovered) => {
          setHovered(hovered);
          onHoverItem?.(hovered ? item.id : undefined);
        }}
        previewed={previewed}
        dimmed={dimmed}
        activateRef={activateRef}
      />
    </Marker>
  );
};
